import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useHistory } from "react-router-dom";

import {
    Box,
    Paper,
    Typography,
    Grid,
    Button,
    Chip,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination
} from "@mui/material";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import SensorsIcon from "@mui/icons-material/Sensors";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import RouteIcon from "@mui/icons-material/Route";
import DownloadIcon from "@mui/icons-material/Download";

import { Map, Marker, GoogleApiWrapper } from "google-maps-react";
import { exportarRutaExcel } from "./createExcel";


import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer
} from "recharts";

import API from "../../services/api-service";

const DetalleRuta = (props) => {

    const location = useLocation();
    const history = useHistory();

    const camion = location.state?.camion || {};
    const ruta = location.state?.ruta || {};
    const [centroMapa, setCentroMapa] = useState({
        lat: -0.2598019,
        lng: -78.5271612
    });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const [detalleRuta, setDetalleRuta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [puntoSeleccionado, setPuntoSeleccionado] = useState(null);


    useEffect(() => {
        const fetchDetalleRuta = async () => {

            try {

                setLoading(true);

                const detalle = await API.getDetalleRuta(ruta);

                setDetalleRuta(detalle);

            } catch (error) {

                console.error(
                    "Error al obtener el detalle de la ruta:",
                    error
                );

                setDetalleRuta(null);

            } finally {

                setLoading(false);

            }
        };

        if (ruta?._id) {
            fetchDetalleRuta();
        } else {
            setDetalleRuta(null);
            setLoading(false);
        }

    }, [ruta?._id]);


    const ubicaciones =
        detalleRuta?.ubicaciones || [];


    const sensores =
        detalleRuta?.sensores || [];

    const datosGrafico = useMemo(() => {

        const timestamps = new Set();

        sensores.forEach(sensor => {

            sensor.datos?.forEach(dato => {
                timestamps.add(dato.timestamp);
            });

        });

        const timestampsOrdenados = Array
            .from(timestamps)
            .sort(
                (a, b) =>
                    new Date(a) - new Date(b)
            );

        return timestampsOrdenados.map(timestamp => {

            const punto = {
                timestamp,
                hora: new Date(timestamp)
                    .toLocaleTimeString()
            };

            sensores.forEach(sensor => {

                const dato = sensor.datos?.find(
                    item => item.timestamp === timestamp
                );

                punto[sensor.deviceID] =
                    dato?.temperatura ?? null;

            });

            return punto;
        });

    }, [sensores]);


    if (loading) {

        return (
            <Box
                sx={{
                    p: 4,
                    textAlign: "center"
                }}
            >
                <Typography>
                    Cargando detalle de la ruta...
                </Typography>
            </Box>
        );

    }


    return (

        <Box
            sx={{
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#f5f7fa",
                p: 3,
            }}
        >


            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >

                <Box>

                    <Typography
                        variant="h4"
                        fontWeight="bold"
                        color="#263238"
                    >
                        Detalle de la ruta
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        Visualización del recorrido y
                        temperatura de los sensores
                    </Typography>

                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                        variant="contained"
                        color="success"
                        startIcon={<DownloadIcon />}
                        onClick={() =>
                            exportarRutaExcel({
                                ruta,
                                ubicaciones,
                                sensores,
                                camion
                            })
                        }
                    >
                        Descargar Excel
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => history.goBack()}
                    >
                        Regresar
                    </Button>
                </Box>

            </Box>

            <Grid
                container
                spacing={2}
                sx={{ mb: 3 }}
            >

                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                >

                    <InfoCard
                        icon={
                            <LocalShippingIcon />
                        }
                        title="Camión"
                        value={
                            camion.placa || "N/A"
                        }
                        color="#1976d2"
                    />

                </Grid>


                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                >

                    <InfoCard
                        icon={
                            <RouteIcon />
                        }
                        title="Gateway"
                        value={
                            detalleRuta?.ruta?.idGateway ||
                            ruta.idGateway ||
                            "N/A"
                        }
                        color="#2e7d32"
                    />

                </Grid>


                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                >

                    <InfoCard
                        icon={
                            <SensorsIcon />
                        }
                        title="Sensores"
                        value={
                            sensores.length
                        }
                        color="#ed6c02"
                    />

                </Grid>


                <Grid
                    item
                    xs={12}
                    sm={6}
                    md={3}
                >

                    <InfoCard
                        icon={
                            <CalendarMonthIcon />
                        }
                        title="Estado"
                        value={
                            detalleRuta?.ruta?.estado ||
                            ruta.estado ||
                            "N/A"
                        }
                        color="#9c27b0"
                    />

                </Grid>

            </Grid>

            <Paper
                elevation={0}
                sx={{
                    p: 2,
                    mb: 3,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                }}
            >

                <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                >
                    Resumen de la ruta
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Grid
                    container
                    spacing={3}
                >

                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Inicio
                        </Typography>

                        <Typography variant="h6">

                            {formatearFecha(
                                detalleRuta?.ruta?.fechaInicio
                            )}

                        </Typography>

                    </Grid>


                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Finalización
                        </Typography>

                        <Typography variant="h6">

                            {formatearFecha(
                                detalleRuta?.ruta?.fechaFin
                            )}

                        </Typography>

                    </Grid>


                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Puntos GPS
                        </Typography>

                        <Typography variant="h6">

                            {ubicaciones.length}

                        </Typography>

                    </Grid>


                    <Grid
                        item
                        xs={12}
                        sm={3}
                    >

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Estado
                        </Typography>

                        <Box sx={{ mt: 1 }}>

                            <Chip
                                label={
                                    detalleRuta?.ruta?.estado ||
                                    "N/A"
                                }
                                color="success"
                                size="small"
                            />

                        </Box>

                    </Grid>

                </Grid>

            </Paper>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    overflow: "hidden",
                    mb: 3,
                }}
            >

                <Box sx={{ p: 2 }}>

                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        Ubicaciones de la ruta
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Posiciones registradas durante el recorrido
                    </Typography>

                </Box>


                <Box
                    sx={{
                        height: 500,
                        width: "100%",
                        position: "relative"
                    }}
                >

                    {ubicaciones.length > 0 ? (

                        <Map
                            google={props.google}
                            zoom={14}
                            initialCenter={{
                                lat: -0.2598019,
                                lng: -78.5271612
                            }}
                            center={centroMapa}
                            containerStyle={{
                                width: "100%",
                                height: "100%"
                            }}
                        >
                            {ubicaciones.map((ubicacion, index) => {

                                const lat = Number(ubicacion.lat);
                                const lng = Number(ubicacion.lng);

                                if (
                                    !Number.isFinite(lat) ||
                                    !Number.isFinite(lng)
                                ) {
                                    console.warn(
                                        "Coordenadas inválidas:",
                                        ubicacion
                                    );

                                    return null;
                                }

                                const seleccionado =
                                    puntoSeleccionado?.timestamp ===
                                    ubicacion.timestamp;

                                return (
                                    <Marker
                                        key={index}
                                        position={{
                                            lat,
                                            lng
                                        }}
                                        title={
                                            seleccionado
                                                ? "Punto seleccionado"
                                                : `Ubicación ${index + 1}`
                                        }
                                        icon={
                                            seleccionado
                                                ? {
                                                    url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png"
                                                }
                                                : undefined
                                        }
                                    />
                                );
                            })}
                        </Map>

                    ) : (

                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#eef2f5"
                            }}
                        >

                            <Typography
                                color="text.secondary"
                            >
                                No existen ubicaciones
                                registradas para esta ruta.
                            </Typography>

                        </Box>

                    )}

                </Box>

            </Paper>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    mb: 3,
                    p: 2,
                }}
            >

                <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={1}
                >
                    Temperatura de los sensores
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={3}
                >
                    Evolución de la temperatura durante
                    el recorrido
                </Typography>


                {datosGrafico.length > 0 ? (

                    <Box
                        sx={{
                            width: "100%",
                            height: 400
                        }}
                    >

                        <ResponsiveContainer
                            width="100%"
                            height="100%"
                        >

                            <LineChart
                                data={datosGrafico}
                                margin={{
                                    top: 10,
                                    right: 30,
                                    left: 10,
                                    bottom: 10
                                }}
                                onClick={(data) => {

                                    if (!data?.activePayload?.length) {
                                        return;
                                    }

                                    // Punto seleccionado del gráfico
                                    const punto = data.activePayload[0].payload;

                                    // Timestamp del punto
                                    const timestamp = punto.timestamp;

                                    // Buscar ubicación correspondiente
                                    const ubicacion = ubicaciones.find(
                                        ubicacion =>
                                            ubicacion.timestamp === timestamp
                                    );

                                    if (!ubicacion) {
                                        console.warn(
                                            "No se encontró ubicación para:",
                                            timestamp
                                        );
                                        return;
                                    }

                                    const lat = Number(ubicacion.lat);
                                    const lng = Number(ubicacion.lng);

                                    if (
                                        !Number.isFinite(lat) ||
                                        !Number.isFinite(lng)
                                    ) {
                                        console.warn(
                                            "Coordenadas inválidas:",
                                            ubicacion
                                        );
                                        return;
                                    }

                                    // Obtener el sensor que se seleccionó
                                    const dataKey =
                                        data.activePayload[0].dataKey;

                                    const temperatura = punto[dataKey];

                                    // Guardamos el punto seleccionado
                                    setPuntoSeleccionado({
                                        timestamp,
                                        lat,
                                        lng,
                                        temperatura,
                                        dataKey
                                    });

                                    // Mover mapa
                                    setCentroMapa({
                                        lat,
                                        lng
                                    });

                                }}
                            >
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                />

                                <XAxis
                                    dataKey="timestamp"
                                    tickFormatter={(timestamp) => {
                                        return new Date(timestamp).toLocaleTimeString("es-EC", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false,
                                            timeZone: "UTC"
                                        });
                                    }}
                                />

                                <YAxis
                                    label={{
                                        value: "°C",
                                        angle: -90,
                                        position: "insideLeft"
                                    }}
                                />

                                <Tooltip
                                    labelFormatter={(timestamp) => {
                                        return new Date(timestamp).toLocaleString("es-EC", {
                                            year: "numeric",
                                            month: "2-digit",
                                            day: "2-digit",
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            second: "2-digit",
                                            hour12: false,
                                            timeZone: "UTC"
                                        });
                                    }}
                                />

                                <Legend />


                                {sensores.map(
                                    (sensor, index) => (

                                        <Line
                                            key={
                                                sensor.deviceID
                                            }
                                            type="monotone"
                                            dataKey={
                                                sensor.deviceID
                                            }
                                            name={
                                                sensor.nombre
                                            }
                                            stroke={
                                                coloresSensor[
                                                index %
                                                coloresSensor.length
                                                ]
                                            }
                                            strokeWidth={3}
                                            dot={false}
                                            connectNulls
                                        />

                                    )
                                )}

                            </LineChart>

                        </ResponsiveContainer>

                    </Box>

                ) : (

                    <Box
                        sx={{
                            height: 300,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#fafafa"
                        }}
                    >

                        <Typography
                            color="text.secondary"
                        >
                            No existen datos de temperatura
                            para esta ruta.
                        </Typography>

                    </Box>

                )}

            </Paper>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    mb: 3,
                    p: 2,
                }}
            >

                <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                >
                    Sensores participantes
                </Typography>

                <Grid
                    container
                    spacing={2}
                >

                    {sensores.map(sensor => (

                        <Grid
                            item
                            xs={12}
                            sm={6}
                            md={4}
                            key={sensor.deviceID}
                        >

                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    border:
                                        "1px solid #e0e0e0",
                                    borderRadius: 2
                                }}
                            >

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 2
                                    }}
                                >

                                    <SensorsIcon
                                        color="primary"
                                    />

                                    <Box>

                                        <Typography
                                            fontWeight="bold"
                                        >
                                            {sensor.nombre}
                                        </Typography>

                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {sensor.deviceID}
                                        </Typography>

                                    </Box>

                                </Box>

                                <Typography
                                    variant="body2"
                                    sx={{ mt: 1 }}
                                >
                                    {sensor.datos?.length || 0}
                                    {" "}mediciones
                                </Typography>

                            </Paper>

                        </Grid>

                    ))}

                </Grid>

            </Paper>

            <Paper
                elevation={0}
                sx={{
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    overflow: "hidden",
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                    >
                        Puntos registrados
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        Historial de posiciones durante el recorrido
                    </Typography>
                </Box>

                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: "#f5f5f5",
                                }}
                            >
                                <TableCell>
                                    <b>Hora</b>
                                </TableCell>

                                <TableCell>
                                    <b>Latitud</b>
                                </TableCell>

                                <TableCell>
                                    <b>Longitud</b>
                                </TableCell>

                                <TableCell>
                                    <b>Temperatura</b>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {ubicaciones
                                .slice(
                                    page * rowsPerPage,
                                    page * rowsPerPage + rowsPerPage
                                )
                                .map((punto, index) => {
                                    const temperatura =
                                        sensores?.[0]?.datos?.find(
                                            (dato) =>
                                                dato.timestamp === punto.timestamp
                                        );

                                    return (
                                        <TableRow
                                            key={punto.timestamp}
                                            hover
                                        >
                                            <TableCell>
                                                {formatearHora(punto.timestamp)}
                                            </TableCell>

                                            <TableCell>
                                                {punto.lat}
                                            </TableCell>

                                            <TableCell>
                                                {punto.lng}
                                            </TableCell>

                                            <TableCell>
                                                {temperatura?.temperatura ?? "N/A"} °C
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={ubicaciones.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="Filas por página"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} de ${count}`
                        }
                    />
                </TableContainer>

            </Paper>

        </Box>
    );
};

const InfoCard = ({
    icon,
    title,
    value,
    color
}) => {

    return (

        <Paper
            elevation={0}
            sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                height: "100%",
            }}
        >

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                }}
            >

                <Box
                    sx={{
                        width: 45,
                        height: 45,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor:
                            `${color}15`,
                        color: color,
                    }}
                >
                    {icon}
                </Box>

                <Box>

                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        {title}
                    </Typography>

                    <Typography
                        variant="body1"
                        fontWeight="bold"
                    >
                        {value}
                    </Typography>

                </Box>

            </Box>

        </Paper>
    );
};

const coloresSensor = [
    "#1976D2",
    "#E53935",
    "#43A047",
    "#FB8C00",
    "#8E24AA",
    "#00897B",
    "#6D4C41"
];

function formatearFecha(fecha) {

    if (!fecha) {
        return "N/A";
    }

    return new Date(fecha).toLocaleString();
}


function formatearHora(fecha) {

    if (!fecha) {
        return "N/A";
    }

    const date = new Date(fecha);

    if (isNaN(date.getTime())) {
        return "N/A";
    }

    return date.toLocaleTimeString("es-EC", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
        timeZone: "UTC"
    });
}

export default GoogleApiWrapper({
    apiKey: "AIzaSyBYNDUEf0g8OQPJfiEW7ybizYPiMN_7XOU"
})(DetalleRuta);
